import React, { useState, useEffect } from "react";
import { Skeleton } from "./ui/skeleton";

interface LoadingWrapperProps {
  children: React.ReactNode;
  loadingDuration?: number; // Durée en millisecondes
  skeletonComponent?: React.ReactNode;
}

/**
 * Composant wrapper qui affiche un skeleton pendant le chargement
 * puis affiche le contenu réel après la durée spécifiée
 */
export const LoadingWrapper: React.FC<LoadingWrapperProps> = ({
  children,
  loadingDuration = 1500, // 1.5 secondes par défaut
  skeletonComponent,
}) => {
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, loadingDuration);

    return () => clearTimeout(timer);
  }, [loadingDuration]);

  if (isLoading) {
    return skeletonComponent || <DefaultSkeleton />;
  }

  return <>{children}</>;
};

/**
 * Skeleton par défaut pour les pages génériques
 */
const DefaultSkeleton: React.FC = () => {
  return (
    <div className="min-h-screen flex flex-col">
      {/* Header skeleton */}
      <div className="h-16 bg-gray-100">
        <Skeleton className="h-full w-full" />
      </div>
      
      {/* Secondary nav skeleton */}
      <div className="h-12 bg-gray-50">
        <Skeleton className="h-full w-full" />
      </div>
      
      {/* Main content skeleton */}
      <div className="flex-grow p-4 md:p-8">
        <div className="max-w-7xl mx-auto space-y-6">
          {/* Title skeleton */}
          <Skeleton className="h-12 w-3/4" />
          
          {/* Content blocks skeleton */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <Skeleton className="h-6 w-full" />
              <Skeleton className="h-6 w-5/6" />
              <Skeleton className="h-6 w-4/5" />
              <Skeleton className="h-32 w-full" />
            </div>
            <div className="space-y-4">
              <Skeleton className="h-6 w-full" />
              <Skeleton className="h-6 w-5/6" />
              <Skeleton className="h-6 w-4/5" />
              <Skeleton className="h-32 w-full" />
            </div>
          </div>
          
          {/* Additional content skeleton */}
          <div className="space-y-4">
            <Skeleton className="h-6 w-full" />
            <Skeleton className="h-6 w-3/4" />
            <Skeleton className="h-24 w-full" />
          </div>
        </div>
      </div>
      
      {/* Footer skeleton */}
      <div className="h-20 bg-gray-100">
        <Skeleton className="h-full w-full" />
      </div>
    </div>
  );
};

/**
 * Skeleton spécialisé pour les pages de dashboard avec cartes
 */
export const DashboardSkeleton: React.FC = () => {
  return (
    <div className="min-h-screen flex flex-col">
      {/* Header skeleton */}
      <div className="h-16 bg-gray-100">
        <Skeleton className="h-full w-full" />
      </div>
      
      {/* Main content skeleton */}
      <div className="flex-grow flex items-center justify-center p-4 md:p-8">
        <div className="w-full max-w-7xl">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
            {Array.from({ length: 4 }).map((_, index) => (
              <div key={index} className="min-h-80 rounded-[30.53px] border-2 border-gray-200 p-8">
                <div className="flex flex-col items-center justify-center h-full space-y-6">
                  <Skeleton className="w-24 h-24 rounded-full" />
                  <Skeleton className="h-6 w-3/4" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
      
      {/* Footer skeleton */}
      <div className="h-20 bg-gray-100">
        <Skeleton className="h-full w-full" />
      </div>
    </div>
  );
};

/**
 * Skeleton spécialisé pour les pages d'accueil avec hero section
 */
export const HeroSkeleton: React.FC = () => {
  return (
    <div className="min-h-screen flex flex-col">
      {/* Header skeleton */}
      <div className="h-16 bg-gray-100">
        <Skeleton className="h-full w-full" />
      </div>
      
      {/* Hero section skeleton */}
      <div className="flex-1 relative bg-gray-200">
        <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-4 md:px-8">
          <Skeleton className="h-16 w-3/4 mb-6" />
          <Skeleton className="h-8 w-2/3 mb-12" />
          <Skeleton className="h-12 w-48 rounded-3xl" />
        </div>
      </div>
      
      {/* Footer skeleton */}
      <div className="h-20 bg-gray-100">
        <Skeleton className="h-full w-full" />
      </div>
    </div>
  );
};